import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import PageHeader from '../../components/common/PageHeader';
import { Download, FileText, Calendar, TrendingUp } from 'lucide-react';

type Report = {
  id: string;
  name: string;
  type: 'daily' | 'monthly' | 'yearly' | 'statement' | 'tax' | 'investor';
  period: string;
  generatedDate: string;
  size: string;
  status: 'ready' | 'generating';
};

const TradingReports = () => {
  const [reportType, setReportType] = useState<
    'all' | 'daily' | 'monthly' | 'yearly' | 'statement' | 'tax' | 'investor'
  >('all');

  const reports: Report[] = [
    {
      id: 'RPT001',
      name: 'Daily Trading Report - Jan 15, 2024',
      type: 'daily',
      period: '2024-01-15',
      generatedDate: '2024-01-15 23:59:00',
      size: '245 KB',
      status: 'ready',
    },
    {
      id: 'RPT002',
      name: 'Monthly Performance Report - January 2024',
      type: 'monthly',
      period: '2024-01',
      generatedDate: '2024-01-31 23:59:00',
      size: '1.2 MB',
      status: 'ready',
    },
    {
      id: 'RPT003',
      name: 'Yearly Summary Report - 2023',
      type: 'yearly',
      period: '2023',
      generatedDate: '2024-01-01 00:00:00',
      size: '5.8 MB',
      status: 'ready',
    },
    {
      id: 'RPT004',
      name: 'Investor Statement - Q4 2023',
      type: 'statement',
      period: '2023-Q4',
      generatedDate: '2024-01-15 10:30:00',
      size: '890 KB',
      status: 'ready',
    },
    {
      id: 'RPT005',
      name: 'Tax Report - 2023',
      type: 'tax',
      period: '2023',
      generatedDate: '2024-01-10 14:20:00',
      size: '2.1 MB',
      status: 'ready',
    },
    {
      id: 'RPT006',
      name: 'Investor Performance Report - John Smith',
      type: 'investor',
      period: '2024-01',
      generatedDate: '2024-01-15 09:45:00',
      size: '456 KB',
      status: 'ready',
    },
  ];

  const filteredReports = reports.filter(
    report => reportType === 'all' || report.type === reportType
  );

  const handleDownload = (reportId: string) => {
    console.log('Downloading report:', reportId);
  };

  const handleGenerateReport = (type: string) => {
    console.log('Generating report:', type);
  };

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Reports"
          description="Generate and download trading service reports"
        />
      </div>

      <Tabs defaultValue="available" className="space-y-6">
        <TabsList>
          <TabsTrigger value="available">Available Reports</TabsTrigger>
          <TabsTrigger value="generate">Generate Reports</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="available" className="space-y-6">
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={reportType === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setReportType('all')}
            >
              All Reports
            </Button>
            <Button
              variant={reportType === 'daily' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setReportType('daily')}
            >
              Daily
            </Button>
            <Button
              variant={reportType === 'monthly' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setReportType('monthly')}
            >
              Monthly
            </Button>
            <Button
              variant={reportType === 'yearly' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setReportType('yearly')}
            >
              Yearly
            </Button>
            <Button
              variant={reportType === 'statement' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setReportType('statement')}
            >
              Statements
            </Button>
            <Button
              variant={reportType === 'tax' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setReportType('tax')}
            >
              Tax Reports
            </Button>
            <Button
              variant={reportType === 'investor' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setReportType('investor')}
            >
              Investor Reports
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredReports.map(report => (
              <Card key={report.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-base">{report.name}</CardTitle>
                      <CardDescription className="mt-1">
                        {report.period}
                      </CardDescription>
                    </div>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        report.status === 'ready'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {report.status.charAt(0).toUpperCase() +
                        report.status.slice(1)}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Generated:</span>
                      <span>
                        {new Date(report.generatedDate).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Size:</span>
                      <span>{report.size}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Type:</span>
                      <span className="capitalize">{report.type}</span>
                    </div>
                    <Button
                      className="w-full"
                      size="sm"
                      onClick={() => handleDownload(report.id)}
                      disabled={report.status !== 'ready'}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="generate" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Daily Report
                </CardTitle>
                <CardDescription>
                  Generate daily trading summary
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  className="w-full"
                  onClick={() => handleGenerateReport('daily')}
                >
                  Generate Daily Report
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Monthly Report
                </CardTitle>
                <CardDescription>
                  Generate monthly performance report
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  className="w-full"
                  onClick={() => handleGenerateReport('monthly')}
                >
                  Generate Monthly Report
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Yearly Report
                </CardTitle>
                <CardDescription>
                  Generate annual summary report
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  className="w-full"
                  onClick={() => handleGenerateReport('yearly')}
                >
                  Generate Yearly Report
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Investor Statement
                </CardTitle>
                <CardDescription>
                  Generate investor account statement
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  className="w-full"
                  onClick={() => handleGenerateReport('statement')}
                >
                  Generate Statement
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Tax Report
                </CardTitle>
                <CardDescription>
                  Generate tax reporting document
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  className="w-full"
                  onClick={() => handleGenerateReport('tax')}
                >
                  Generate Tax Report
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Custom Report
                </CardTitle>
                <CardDescription>
                  Generate custom report with filters
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  className="w-full"
                  onClick={() => handleGenerateReport('custom')}
                >
                  Create Custom Report
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="scheduled" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Scheduled Reports</CardTitle>
              <CardDescription>
                Configure automated report generation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-semibold">Daily Trading Summary</h4>
                    <p className="text-sm text-muted-foreground">
                      Generated every day at 23:59 UTC
                    </p>
                  </div>
                  <Button variant="outline" size="sm">
                    Configure
                  </Button>
                </div>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-semibold">
                      Monthly Performance Report
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Generated on the last day of each month
                    </p>
                  </div>
                  <Button variant="outline" size="sm">
                    Configure
                  </Button>
                </div>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-semibold">Investor Statements</h4>
                    <p className="text-sm text-muted-foreground">
                      Generated quarterly on the 15th
                    </p>
                  </div>
                  <Button variant="outline" size="sm">
                    Configure
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TradingReports;
